import type { ComponentPropsWithoutRef } from 'react';
import { useMemo } from 'react';

import { Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { Button } from '../../shared/components/UI/Button/Button';
import { NAVBAR_HEIGHT } from '../../shared/components/UI/Navbar/Navbar';

import { BID_RESPONSE_SIDEBAR_WIDTH } from '../../features/bidResponse/components/sidebar/SideBar';
import { BidResponseTab } from '../../features/bidResponse/components/vault/components/BidResponseTab';
import { CreateBidResponseModal } from '../../features/bidResponse/components/vault/components/CreateBidResponseModal';
import { VaultTabs } from '../../features/bidResponse/components/vault/components/VaultTabs';
import { WorkspaceDocumentsTab } from '../../features/bidResponse/components/vault/components/WorkspaceDocumentTab';
import { useWorkspaceDocumentsQuery } from '../../features/bidResponse/components/vault/hooks/workspaceDocuments.hook';

export const VAULT_TABS_HEIGHT = 145;

export function VaultPage() {
  const { t } = useTranslation(['bidResponse']);
  const { pathname } = useLocation();
  const { data: documents } = useWorkspaceDocumentsQuery();
  const isBidResponseTab = matchPath('/response/vault/bid-responses', pathname);

  const tabs: ComponentPropsWithoutRef<typeof VaultTabs>['tabs'] = useMemo(
    () => [
      {
        id: 'bid-responses',
        label: t('vault.tabs.bidResponse'),
        path: '/response/vault/bid-responses',
        component: <BidResponseTab />,
      },
      {
        id: 'workspace-documents',
        label: t('vault.tabs.workspaceDocuments', { count: documents.length }),
        path: '/response/vault/workspace-documents',
        component: <WorkspaceDocumentsTab />,
      },
    ],
    [t, documents],
  );

  return (
    <Stack
      w={`calc(100vw - ${BID_RESPONSE_SIDEBAR_WIDTH}px)`}
      miw="100%"
      p="05"
      spacing="04"
      h={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      sx={{
        overflow: 'hidden',
      }}
    >
      <Group noWrap spacing="01" align="center" position="apart">
        <Text size="md" c="gray.9">
          {t('vault.title')}
        </Text>
        <CreateBidResponseButton enabled={!!isBidResponseTab} />
      </Group>
      <VaultTabs tabs={tabs} />
    </Stack>
  );
}

function CreateBidResponseButton({ enabled }: { enabled: boolean }) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation(['bidResponse']);

  /** keep this instead of return null to avoid layout shift */
  if (!enabled) {
    return (
      <Button sx={{ visibility: 'hidden', pointerEvent: 'none' }}>
        {t('vault.addBidResponseCTA')}
      </Button>
    );
  }

  return (
    <>
      <CreateBidResponseModal opened={opened} onClose={close} />
      <Button onClick={open}>{t('vault.addBidResponseCTA')}</Button>
    </>
  );
}
